import React from 'react';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Cloud,
  CloudRain,
  Thermometer,
  Droplets,
  Wind,
  Eye,
  AlertCircle
} from 'lucide-react';
import { useMonthlyWeather } from '@/hooks/useMonthlyWeather';
import { useUnitPreferences } from '@/hooks/useUnitPreferences';

interface MonthlyWeatherSummaryProps {
  locationId: string;
  month: number;
  year: number;
  locationName: string;
}

interface MonthlyWeatherData {
  location_id: string;
  user_id: string;
  month: number;
  temperature_avg: number;
  temperature_min: number;
  temperature_max: number;
  precipitation_avg: number;
  humidity_avg: number;
  cloud_cover_avg: number;
  wind_speed_avg: number;
  pressure_avg?: number;
  data_source: string;
  years_included?: number[];
  years_count?: number;
  data_quality_score?: number;
}

export const MonthlyWeatherSummary: React.FC<MonthlyWeatherSummaryProps> = ({
  locationId,
  month,
  year,
  locationName
}) => {
  const { data: weatherData, loading, error, retry } = useMonthlyWeather({
    locationId,
    month,
    year,
    enabled: true
  });
  
  const { 
    preferredUnits, 
    setPreferredUnits,
    formatTemperature, 
    formatPrecipitation, 
    formatWindSpeed 
  } = useUnitPreferences();

  const getCloudCoverIcon = (cloudCover: number) => {
    if (cloudCover < 25) return <Eye className="w-3.5 h-3.5 text-green-500" />;
    if (cloudCover < 50) return <Cloud className="w-3.5 h-3.5 text-yellow-500" />;
    if (cloudCover < 75) return <Cloud className="w-3.5 h-3.5 text-orange-500" />;
    return <Cloud className="w-3.5 h-3.5 text-red-500" />;
  };

  const getCloudCoverText = (cloudCover: number) => {
    if (cloudCover < 25) return 'Excellent';
    if (cloudCover < 50) return 'Good';
    if (cloudCover < 75) return 'Fair';
    return 'Poor';
  };

  const getVisibilityColor = (cloudCover: number) => {
    if (cloudCover < 25) return 'text-green-600 bg-green-50 border-green-200';
    if (cloudCover < 50) return 'text-yellow-600 bg-yellow-50 border-yellow-200';
    if (cloudCover < 75) return 'text-orange-600 bg-orange-50 border-orange-200';
    return 'text-red-600 bg-red-50 border-red-200';
  };

  if (loading) {
    return (
      <div className="flex items-center gap-3 px-3 py-2 rounded-lg bg-muted/40 border border-border/50">
        <Skeleton className="h-4 w-full max-w-md" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-orange-50 border border-orange-200 text-xs">
        <AlertCircle className="w-3.5 h-3.5 text-orange-500 flex-shrink-0" />
        <span className="text-orange-700">Weather unavailable</span>
        <button onClick={retry} className="text-orange-600 hover:text-orange-800 underline ml-1">
          Retry
        </button>
      </div>
    );
  }

  if (!weatherData) {
    return null;
  }

  return (
    <div className="flex items-center gap-4 flex-wrap px-4 py-3 rounded-lg bg-muted/40 border border-border/50 text-sm text-foreground/80">
      <Cloud className="w-4 h-4 text-gold flex-shrink-0" />
      <span className="text-muted-foreground font-medium">
        {['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][month]} climate avg:
      </span>
      <span className="flex items-center gap-1.5">
        <Thermometer className="w-3.5 h-3.5 text-muted-foreground" />
        {formatTemperature(weatherData.temperature_avg)}
      </span>
      <span className="text-border">·</span>
      <span className="flex items-center gap-1.5">
        <CloudRain className="w-3.5 h-3.5 text-muted-foreground" />
        {formatPrecipitation(weatherData.precipitation_avg)}
      </span>
      <span className="text-border">·</span>
      <span className="flex items-center gap-1.5">
        <Droplets className="w-3.5 h-3.5 text-muted-foreground" />
        {weatherData.humidity_avg}%
      </span>
      <span className="text-border">·</span>
      <span className="flex items-center gap-1.5">
        <Wind className="w-3.5 h-3.5 text-muted-foreground" />
        {formatWindSpeed(weatherData.wind_speed_avg)}
      </span>
      <span className="text-border">·</span>
      <span className="flex items-center gap-1.5">
        {getCloudCoverIcon(weatherData.cloud_cover_avg)}
        <Badge
          variant="outline"
          className={`text-xs px-1.5 py-0 ${getVisibilityColor(weatherData.cloud_cover_avg)}`}
        >
          {getCloudCoverText(weatherData.cloud_cover_avg)} clarity
        </Badge>
      </span>
      <button
        onClick={() => setPreferredUnits(preferredUnits === 'imperial' ? 'metric' : 'imperial')}
        className="ml-auto text-xs text-muted-foreground hover:text-foreground transition-colors underline"
      >
        {preferredUnits === 'imperial' ? '°C' : '°F'}
      </button>
    </div>
  );
};